/**
 * apps/web :: lib/push-subscribe
 *
 * Client helpers that tie together:
 *   1. the Notification + Push Manager + Service Worker APIs,
 *   2. the VAPID public key fetched from
 *      `GET /api/notifications/push/public-key`, and
 *   3. the `POST /api/notifications/push/subscribe` backend route.
 *
 * Exposed API:
 *   - `isPushSupported()`         : feature-detection gate
 *   - `subscribeToPush(label?)`   : request permission → subscribe → POST
 *   - `unsubscribeFromPush()`     : active sub → unsubscribe → DELETE ALL
 *   - `PushPermissionDeniedError` : typed error caller can catch
 *
 * Secrecy:
 *   - The subscription `keys.p256dh` + `keys.auth` ride exactly one hop
 *     (browser → backend) and are never logged. Vitest mocks assert this
 *     boundary.
 */
import { apiFetch } from '@/lib/api';
import { getPushPublicKey } from '@/lib/notifications-api';

/** Typed error so UI can special-case "permission was denied". */
export class PushPermissionDeniedError extends Error {
  constructor(message = 'Push notifications permission was denied.') {
    super(message);
    this.name = 'PushPermissionDeniedError';
  }
}

/** Typed error when the deploy isn't VAPID-configured. */
export class PushNotConfiguredError extends Error {
  constructor(message = 'Push notifications are not configured on this deploy.') {
    super(message);
    this.name = 'PushNotConfiguredError';
  }
}

/**
 * Feature-detect all three APIs the flow needs. One false → the InAppPush
 * toggle must hide (graceful degradation per the hard-rules).
 */
export function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (typeof window.PushManager === 'undefined') return false;
  if (typeof window.Notification === 'undefined') return false;
  return true;
}

/**
 * Base64url → Uint8Array for `applicationServerKey`. The browser insists
 * on a raw byte-array, not the base64-url string the backend ships.
 *
 * Exported for unit tests — the conversion is the subtle bit where a
 * misplaced `-`/`_` or wrong padding breaks subscribe silently.
 */
export function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/**
 * Derives a short human-readable device label from `navigator.userAgent`.
 * We keep this very rough — "Chrome on macOS" is plenty. The goal is
 * distinguishability in the devices list, not forensic accuracy.
 */
export function deriveDeviceLabel(userAgent: string = navigator.userAgent ?? ''): string {
  const ua = userAgent;
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  let os = 'Device';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iOS/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  return `${browser} on ${os}`;
}

interface SubscribeResponse {
  id: string;
}

/**
 * Happy-path flow:
 *   1. request Notification permission (throws PushPermissionDeniedError
 *      on 'denied'),
 *   2. wait for the service worker to be ready,
 *   3. fetch the VAPID public key,
 *   4. subscribe via the PushManager,
 *   5. POST the subscription to the backend.
 *
 * Returns the server-assigned subscription id so callers can display it.
 */
export async function subscribeToPush(
  deviceLabel: string = deriveDeviceLabel(),
): Promise<string> {
  if (!isPushSupported()) {
    throw new PushNotConfiguredError('This browser does not support push notifications.');
  }

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    throw new PushPermissionDeniedError();
  }
  if (permission !== 'granted') {
    // 'default' — user dismissed the prompt. Treat as denied for UI purposes;
    // the user can retry by flipping the toggle again.
    throw new PushPermissionDeniedError(
      'Push notifications permission was not granted.',
    );
  }

  const registration = await navigator.serviceWorker.ready;

  let publicKey: string;
  try {
    const { publicKey: fetched } = await getPushPublicKey();
    publicKey = fetched;
  } catch (err) {
    throw new PushNotConfiguredError(
      err instanceof Error ? err.message : 'Could not fetch VAPID public key.',
    );
  }

  const applicationServerKey = urlBase64ToUint8Array(publicKey);

  // Reuse any existing subscription — re-POSTing is safe (backend upserts)
  // but avoids an unnecessary push-service round-trip.
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast to BufferSource — the DOM lib's `applicationServerKey` type
      // bounces between ArrayBuffer-backed Uint8Array flavours across TS
      // versions. Runtime behaviour is identical.
      applicationServerKey: applicationServerKey as BufferSource,
    });
  }

  const json = subscription.toJSON();
  const endpoint = json.endpoint ?? subscription.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) {
    throw new Error('Browser returned an incomplete push subscription.');
  }

  const body = {
    endpoint,
    p256dh,
    auth,
    deviceLabel,
  };
  const result = await apiFetch<SubscribeResponse>(
    '/api/notifications/push/subscribe',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
  return result.id;
}

/**
 * Revoke the active browser subscription AND tell the backend to drop its
 * row(s). Idempotent — calling when there's no subscription is a no-op.
 *
 * We intentionally DELETE-all rather than DELETE-by-id: the browser only
 * knows one subscription per SW scope, and the spec's "turn off" UX should
 * match the user's mental model ("stop pushing to this device").
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  } catch (err) {
    // Best-effort on the browser side; we still want to clear the server rows.
    // eslint-disable-next-line no-console
    console.warn('[push-subscribe] browser unsubscribe failed', err);
  }

  try {
    await apiFetch('/api/notifications/push/subscribe', { method: 'DELETE' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[push-subscribe] server unsubscribe failed', err);
  }
}
